import { Button, Chip, MenuItem, Popover, Select, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import FullTaskTopbar from "../../Components/Topbars/FullTaskTopbar";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { assetService, cacheService, hlsChunkerService, mediaService } from "../../Core/Api/services";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { FileAccessibility } from "../../Core/Api/Types/file-accessibility";
import { FileCategory } from "../../Core/Api/Types/file-category";
import { FileStatus } from "../../Core/Api/Types/file-status";
import { v4 as uuidv4 } from "uuid";
import { FrameToTimecode, SecToFrame, SecToTimecode, TimecodeToSec } from "../../Utils/Timecode/ReactPlayer";
import { SubtitlesList } from "./subtitles-list";
import { produce } from "immer";
import { DbFileInfos } from "../../Core/Api/Types/db-file-infos";
import { PeaksWrapper } from "./peaks-wrapper";
import { LoadingTimeline } from "./loading-timeline";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import { ScribetoolFooter } from "./scribetool-footer";
import { ObjectType } from "../../Core/Api/Types/object-type";
import { routePaths } from "../../Router/router";
import useEventListener from "../../Utils/Hooks/useEventListener";
import QuestionMarkOutlined from "@mui/icons-material/QuestionMarkOutlined";
import { useTranslation } from "react-i18next";
export interface UpdateCaptionsEventProps {
  type: "update" | "create" | "delete";
  segmentId: string;
  data?: any;
}

export interface Caption {
  segmentId: string;
  cue?: VTTCue;
  style: string;
  tcIn: string;
  tcOut: string;
  secIn: number;
  secOut: number;
  note?: string | undefined;
  status?: string | undefined;
  speaker?: string | undefined;
  words?: { start: number; end: number; text: string }[];
  text?: string;
}

export const Scribetool: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [asset, setAsset] = useState<DbAssetObjectInfos | undefined | null>();
  const [errors, setErrors] = useState<any[]>([]);
  const [subtitlesData, setSubtitlesData] = useState<any>();
  const [subtitlesDataList, setSubtitlesDataList] = useState<any[]>([]);
  const [availableTtmlFiles, setAvailableTtmlFiles] = useState<DbFileInfos[]>([]);
  const [selectedTtmlFileId, setSelectedTtmlFileId] = useState<number | null>(null);
  const [waveformFile, setWaveformFile] = useState<DbFileInfos | undefined>();
  const [videoProxy, setVideoProxy] = useState<any>();
  const [videoAsset, setVideoAsset] = useState<DbAssetObjectInfos | null>(null);
  const [captions, setCaptions] = useState<Caption[]>([]);
  const [updateCaptionEvent, setUpdateCaptionEvent] = useState<UpdateCaptionsEventProps | null>(null);
  const [currentTime, setCurrentTime] = useState<any>();
  const [currentCaptionIndex, setCurrentCaptionIndex] = useState(-1);
  const [trackId, setTrackId] = useState<string>("");
  const [triggerUpdate, setTriggerUpdate] = useState<boolean>(false);
  const [editSegmentId, setEditSegmentId] = useState<string | null>(null);
  const [dragSegmentId, setDragSegmentId] = useState<string | null>(null);
  const [duration, setDuration] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [mode, setMode] = useState<"unknown" | "stp" | "sub" | "project">("unknown");
  const [showTimeline, setShowTimeline] = useState<boolean>(true);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const popoverId = open ? "helper-popover" : undefined;

  const currentTextTrack = useRef<any>(undefined);
  const videoRef = useRef<any>(undefined);
  const captionsRef = useRef<any>(undefined);
  const id = parseInt(params.assetId ?? "0");

  const videoSrc = useMemo(() => {
    if ((!videoAsset && mode !== "project") || !videoProxy) return undefined;
    return `${hlsChunkerService.GetServiceUrl()}/${videoProxy.id}/${cacheService.appData?.platformId}`;
  }, [videoProxy]);

  const videoFps = useMemo(() => {
    return videoProxy?.properties?.duration?.rate || 25;
  }, [videoProxy]);

  const handleShortcut = (e: KeyboardEvent) => {
    if (!videoRef.current || editSegmentId !== null) return;
    switch (e.key) {
      case ";":
        const closerCaptionEndIndex = captions.findLastIndex((x) => x.secOut < TimecodeToSec(currentTime, videoFps));

        if (closerCaptionEndIndex >= 0) videoRef.current.currentTime = captions[closerCaptionEndIndex].secOut;
        break;
      case ":":
        const closerCaptionStartIndex = captions.findIndex((x) => x.secIn > TimecodeToSec(currentTime, videoFps));

        if (closerCaptionStartIndex >= 0) videoRef.current.currentTime = captions[closerCaptionStartIndex].secIn;
        break;
      case "k":
        videoRef.current.blur();
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
        break;
      default:
        break;
    }
  };

  useEventListener("keydown", handleShortcut);

  const checkForSubProxy = (mode: "project" | "sub") => {
    mediaService.GetFilesByObjectId(id).then((res) => {
      if (!res.success) {
        setErrors((x) => [...x, { step: "ttmlFile", message: res.message }]);
        return;
      }
      const ttmlFileIds =
        res.files?.filter(
          (x) =>
            (x.name?.split(".").pop() === "ttml" || x.name?.split(".").pop() === "vtt") &&
            x.accessibility === FileAccessibility.Disk &&
            x.category === FileCategory.Proxy
        ) ?? [];
      if (ttmlFileIds.length === 0) {
        setErrors((x) => [...x, { step: "ttmlFile", message: t("page:loadingTimeline.error.noLinkedTtmlProxy") }]);
        return;
      }

      setAvailableTtmlFiles(ttmlFileIds || []);

      if (mode === "project") {
        const foundProxy = res.files?.find(
          (x) =>
            x.accessibility === FileAccessibility.Disk &&
            x.category === FileCategory.Proxy &&
            x.status === FileStatus.Online &&
            x.extension === ".mp4"
        );
        if (!foundProxy) {
          setErrors((x) => [...x, { step: "videoProxy", message: t("page:loadingTimeline.error.noLinkedVideoProxy") }]);
          return;
        }
        setVideoProxy(foundProxy);
        const waveformFile = res.files!.find((f) => f.linkedType == "WaveformData");
        setWaveformFile(waveformFile);
      }
    });
  };

  const checkForVideoProxy = () => {
    if (!asset || !asset.parentObjectId) return;
    assetService.GetGraph(asset.parentObjectId, false, true, false).then((res) => {
      if (!res.success) {
        setErrors((x) => [...x, { step: "videoAsset", message: res.message }]);
        return;
      }
      const content = res.graph?.childObjects?.find((x) => x.metadata?.find((m) => m.fieldName === "refVideoAssetIds"));
      if (!content) return;
      const ref = content.metadata!.find((m) => m.fieldName === "refVideoAssetIds")?.value;
      const foundVideo = content.childObjects?.find((x) => ref.includes(x.id));
      if (!foundVideo) {
        setErrors((x) => [
          ...x,
          { step: "videoAsset", message: t("page:loadingTimeline.error.videoReferenceNotFound") },
        ]);
        return;
      }
      setVideoAsset(foundVideo);
      getVideoProxy(foundVideo.id);
    });
  };

  const getVideoProxy = (foundVideoId) => {
    mediaService.GetFilesByObjectId(foundVideoId).then((response) => {
      if (!response.success) {
        setErrors((x) => [...x, { step: "videoProxy", message: response.message }]);
        return;
      }
      const foundProxy = response.files?.find(
        (x) =>
          x.accessibility === FileAccessibility.Disk &&
          x.category === FileCategory.Proxy &&
          x.status === FileStatus.Online
      );
      if (!foundProxy) {
        setErrors((x) => [...x, { step: "videoProxy", message: t("page:loadingTimeline.error.noLinkedVideoProxy") }]);
        return;
      }
      setVideoProxy(foundProxy);
      const waveformFile = response.files!.find((f) => f.linkedType == "WaveformData");
      setWaveformFile(waveformFile);
    });
  };
  function convertSpeetchToTextToCues(subtitltes) {
    var captions: any[] = [];
    subtitltes.speech.speaker.forEach((x, i) => {
      let sentence: any[] = [];
      x.words.forEach((w, n) => {
        if (sentence.length === 0) sentence.push(w);
        else if (w.start === sentence[sentence.length - 1].end) {
          sentence.push(w);
        } else {
          const sentenceStart = sentence[0].start;
          const sentenceEnd = sentence[sentence.length - 1].end;

          var cue = new VTTCue(sentenceStart / videoFps, sentenceEnd / videoFps, sentence.map((x) => x.text).join(""));
          var segmentId = uuidv4();
          cue.id = i + "-" + n;
          captions.push({
            cue: cue,
            style: "Normal",
            tcIn: SecToTimecode(sentenceStart / videoFps, videoFps),
            tcOut: SecToTimecode(sentenceEnd / videoFps, videoFps),
            secIn: sentenceStart / videoFps,
            secOut: sentenceEnd / videoFps,
            note: "",
            status: "",
            segmentId: segmentId,
            speaker: x.name,
            words: sentence,
          });
          sentence = [];
        }
      });
    });
    return captions.sort((a, b) => a.secIn - b.secIn);
  }

  function convertSubtitleMetadataToCues(subtitles) {
    var captions: any[] = [];
    subtitles.rows.forEach((row) => {
      var cue = new VTTCue(row.frameIn / videoFps, row.frameOut / videoFps, row.texts?.map((t) => t.text).join("\r\n"));

      if (row.texts && row.texts.length > 0) {
        cue.line = row.texts[0].vAlign === "Top" ? 2 : row.texts[0].vAlign === "Middle" ? 8 : "auto";
        cue.align = row.texts[0].hAlign === "Start" ? "start" : row.texts[0].hAlign === "End" ? "end" : "center";
      }
      var segmentId = uuidv4();
      cue.id = row.$id;
      captions.push({
        cue: cue,
        style: row.texts && row.texts.length > 0 ? row.texts[0].style : "Normal",
        tcIn: SecToTimecode(row.frameIn / videoFps, videoFps),
        tcOut: SecToTimecode(row.frameOut / videoFps, videoFps),
        secIn: row.frameIn / videoFps,
        secOut: row.frameOut / videoFps,
        note: row.notes,
        status: row.status,
        segmentId: segmentId,
      });
    });
    return captions.sort((a, b) => a.secIn - b.secIn);
  }

  const initTextTrack = () => {
    if (currentTextTrack.current) currentTextTrack.current.mode = "hidden";
    let track = videoRef.current.addTextTrack("captions", "Captions", "en");
    videoRef.current.ontimeupdate = () => {
      const secs = videoRef.current.currentTime;
      const frames = SecToFrame(secs, videoFps);
      setCurrentTime(FrameToTimecode(frames, videoFps));
      setCurrentCaptionIndex(
        captionsRef.current?.findIndex((x) => x.secIn - 0.01 <= secs && x.secOut + 0.01 >= secs) || -1
      );
    };

    videoRef.current.onplay = () => {
      setIsPlaying(true);
    };
    videoRef.current.onpause = () => {
      setIsPlaying(false);
    };
    track.mode = "showing";
    setTrackId(track.id);
    const capt =
      mode === "stp" ? convertSpeetchToTextToCues(subtitlesData) : convertSubtitleMetadataToCues(subtitlesData);
    capt.forEach((c) => {
      track.addCue(c.cue);
    });
    setCaptions(capt);
    currentTextTrack.current = track;
  };

  const onUpdateCaption = (segmentId: string, text: string) => {
    setCaptions(
      produce((draft) => {
        const targetCaption = draft.find((x) => x.segmentId === segmentId);
        if (targetCaption?.text) targetCaption.text = String(text);
        if (targetCaption && targetCaption.cue) targetCaption.cue.text = String(text);
      })
    );
    setTriggerUpdate((x) => !x);
  };

  const onDeletaCaption = (segmentId: string) => {
    const caption = captions.find((x) => x.segmentId === segmentId);
    if (caption && currentTextTrack.current) currentTextTrack.current.removeCue(caption.cue);
    setCaptions((x) => x.filter((c) => c.segmentId !== segmentId));
    setUpdateCaptionEvent({ type: "delete", segmentId: segmentId });

    setTriggerUpdate((x) => !x);
  };

  const onCreateCaption = () => {
    const currentSec = TimecodeToSec(currentTime, videoFps);
    //var cue = new VTTCue(currentSec, currentSec + 1, "");
    var segmentId = uuidv4();
    var newCaption: Caption = {
      //cue: cue,
      style: "Normal",
      tcIn: SecToTimecode(currentSec, videoFps),
      tcOut: SecToTimecode(currentSec + 1, videoFps),
      secIn: currentSec,
      secOut: currentSec + 1,
      note: "",
      status: "",
      segmentId: segmentId,
    };
    const newCaptions = [...(captionsRef.current ?? []), newCaption].sort((a, b) => a.secIn - b.secIn);
    setCaptions(newCaptions);
    captionsRef.current = newCaptions;
    //currentTextTrack.current && currentTextTrack.current.addCue(cue);
    setTriggerUpdate((x) => !x);
    setUpdateCaptionEvent({ type: "create", segmentId: segmentId, data: newCaption });
    setEditSegmentId(segmentId);
  };

  const onClickTimecode = (sec: number) => {
    if (videoRef.current) videoRef.current.currentTime = sec;
  };

  const handleSave = () => {};

  useEffect(() => {
    if (!id) return;
    // Loading asset
    assetService.GetObjectById(id).then((res) => {
      if (!res.success) {
        setErrors((x) => [...x, { step: "asset", message: res.message }]);
        return;
      }
      if (
        res.graph?.type !== ObjectType.Video &&
        res.graph?.type !== ObjectType.Subtitle &&
        res.graph?.type !== ObjectType.ScribeSubtitleProject
      ) {
        setErrors((x) => [
          ...x,
          { step: "asset", message: t("page:loadingTimeline.error.assetTypeMismatch", { type: res.graph?.type }) },
        ]);
        return;
      }
      setAsset(res.graph);

      const subtitlesMetadata = res.graph?.metadata?.find(
        (x) => x.fieldType === BaseMetadataType.DbSpeechToTextMetadataValueInfos
      );
      // Choose mode base on presence of speech to text metadata on asset
      if (subtitlesMetadata) {
        setMode("stp");
        setSubtitlesData(subtitlesMetadata.value);
      } else {
        const newMode = res.graph.type === ObjectType.ScribeSubtitleProject ? "project" : "sub";
        setMode(newMode);
        checkForSubProxy(newMode);
      }
    });
  }, [id]);

  useEffect(() => {
    // select first ttml file when found ttml files
    if (!subtitlesData && availableTtmlFiles.length > 0) {
      setSelectedTtmlFileId(availableTtmlFiles[0].id);
      setSubtitlesDataList(availableTtmlFiles.map((x) => undefined));
    }
  }, [availableTtmlFiles, subtitlesData]);

  useEffect(() => {
    if (!selectedTtmlFileId) return;
    // Load sub file as metadata when selected ttml file change
    const foundSubData =
      availableTtmlFiles.findIndex((x) => x.id === selectedTtmlFileId) > -1
        ? subtitlesDataList[availableTtmlFiles.findIndex((x) => x.id === selectedTtmlFileId)]
        : undefined;
    if (!foundSubData)
      mediaService.GetSubtitleFileAsMetadata(selectedTtmlFileId).then((response) => {
        if (!response.success) {
          setErrors((x) => [...x, { step: "subtitlesData", message: response.message }]);
          return;
        }
        setSubtitlesData(response.subtitles);
        setSubtitlesDataList(
          produce((draft) => {
            if (availableTtmlFiles.findIndex((x) => x.id === selectedTtmlFileId) > -1) {
              draft[availableTtmlFiles.findIndex((x) => x.id === selectedTtmlFileId)] = response.subtitles;
            }
          })
        );
      });
    else {
      setSubtitlesData(foundSubData);
    }
  }, [selectedTtmlFileId]);

  useEffect(() => {
    if (!asset || !subtitlesData || videoProxy) return;
    // after loading subtitles data, try to find video proxy
    if (mode === "stp") {
      setVideoAsset(asset);
      getVideoProxy(asset.id);
    } else if (mode === "sub") checkForVideoProxy();
  }, [asset, subtitlesData, videoProxy]);

  useEffect(() => {
    if (videoSrc === "" || !videoRef.current || !subtitlesData) return;
    initTextTrack();
  }, [videoSrc, videoRef.current, subtitlesData]);

  useEffect(() => {
    captionsRef.current = captions;
  }, [captions, triggerUpdate]);

  return (
    <Stack sx={{ backgroundColor: theme.palette.mode === "dark" ? "black" : "white" }} height={"100vh"}>
      <FullTaskTopbar
        backRoute={routePaths.asset.path + "/" + id}
        rightContent={
          <Stack direction="row" display="flex" gap={1.5}>
            {(mode === "sub" || mode === "project") && (
              <Select
                sx={{ maxWidth: "30vw" }}
                value={selectedTtmlFileId}
                onChange={(e) => setSelectedTtmlFileId((e.target.value as number) || null)}
              >
                {availableTtmlFiles?.map((x) => (
                  <MenuItem value={x.id}>{x.name}</MenuItem>
                ))}
              </Select>
            )}
            <Button
              color="info"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              aria-describedby={popoverId}
              endIcon={<QuestionMarkOutlined />}
              sx={{ zIndex: 1200 }}
            >
              {t("button:helper")}
            </Button>
            <Popover
              id={popoverId}
              open={open}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <Stack spacing={1} m={1.5} width={"300px"}>
                <Typography width={"100%"} textAlign={"center"} variant="h6">
                  {t("page:body.shortcuts")}
                </Typography>
                <Stack
                  mt={"12px !important"}
                  height={"24px"}
                  direction={"row"}
                  display={"flex"}
                  justifyContent={"space-between"}
                >
                  <Chip label="K" />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("common:play")} / {t("common:pause")}
                  </Typography>
                </Stack>
                <Stack height={"24px"} direction={"row"} display={"flex"} justifyContent={"space-between"}>
                  <Chip label=";" />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("page:body.goToPreviousCaption")}
                  </Typography>
                </Stack>
                <Stack height={"24px"} direction={"row"} display={"flex"} justifyContent={"space-between"}>
                  <Chip label=":" />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("page:body.goToNextCaption")}
                  </Typography>
                </Stack>
              </Stack>
            </Popover>
            <Tooltip title={showTimeline ? t("tooltip:title.hideTimeline") : t("tooltip:title.showTimeline")}>
              <Button
                color="info"
                onClick={() => setShowTimeline((x) => !x)}
                variant={!showTimeline ? "contained" : "outlined"}
              >
                <ViewTimelineIcon />
              </Button>
            </Tooltip>
            <Button disabled variant="contained" color="primary">
              {t("common:save")}
            </Button>
          </Stack>
        }
        leftContent={
          <Typography
            overflow={"hidden"}
            whiteSpace={"nowrap"}
            sx={{ maxWidth: "30vw" }}
            textOverflow={"ellipsis"}
            variant="body1"
            color={"text.tier"}
          >
            {asset?.name}
          </Typography>
        }
        title={t("common:scribetool")}
      />
      <Stack
        spacing={1}
        display={"flex"}
        direction={"column"}
        height={"calc(100% - 88px)"}
        marginTop={"72px"}
        marginBottom={showTimeline ? 0 : "45px"}
        overflow={"hidden"}
        ml={1.3}
        mr={1.3}
      >
        <Stack
          height={"-webkit-fill-available"}
          maxHeight={showTimeline ? "calc(100vh - 336px)" : "initial"}
          width={"100%"}
          display={"flex"}
          direction={"row"}
          spacing={1}
        >
          <Stack
            p={1}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.background.default,
              border: theme.palette.mode === "dark" ? "0px solid default" : `1px solid ${theme.palette.grey[300]}`,
            }}
            position={"relative"}
            width={"50%"}
          >
            <Stack
              sx={{ position: "absolute", top: "7px", left: "4px" }}
              spacing={1}
              display={"flex"}
              direction={"row"}
            >
              <Chip sx={{ position: "relative", bottom: "3px" }} label={currentTime} />
            </Stack>

            {videoSrc !== "" && (
              <video height={"100%"} ref={videoRef} controls src={videoSrc + "/" + cacheService.GetAuthKey()} />
            )}
          </Stack>
          <Stack
            pt={1}
            pb={1}
            pl={1}
            sx={{
              //   backgroundColor:
              //     theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.background.default,
              border: theme.palette.mode === "dark" ? "0px solid default" : `1px solid ${theme.palette.grey[300]}`,
            }}
            height={"calc(100% - 32px)"}
            position={"relative"}
            width={"50%"}
          >
            {mode !== "unknown" ? (
              <SubtitlesList
                captions={captions}
                editSegmentId={editSegmentId}
                setEditSegmentId={setEditSegmentId}
                onDeletaCaption={onDeletaCaption}
                onUpdateCaption={onUpdateCaption}
                onCreateCaption={onCreateCaption}
                triggerUpdate={triggerUpdate}
                dragSegmentId={dragSegmentId}
                currentCaptionIndex={currentCaptionIndex}
                onClickTimecode={onClickTimecode}
                currentTime={TimecodeToSec(currentTime, videoFps)}
                videoFps={videoFps}
                mode={mode}
              />
            ) : (
              <></>
            )}
          </Stack>
        </Stack>
        {showTimeline && (
          <PeaksWrapper
            captions={captions}
            captionsRef={captionsRef}
            duration={duration}
            assetId={mode === "project" ? asset?.id : videoAsset?.id}
            setCaptions={setCaptions}
            setDragSegmentId={setDragSegmentId}
            setDuration={setDuration}
            triggerUpdate={triggerUpdate}
            setTriggerUpdate={setTriggerUpdate}
            videoFps={videoFps}
            videoRef={videoRef}
            waveformFile={waveformFile}
            showTimeline={showTimeline}
            currentTextTrack={currentTextTrack}
            updateCaptionEvent={updateCaptionEvent}
            mode={mode}
          />
        )}
      </Stack>
      {loading ? (
        <LoadingTimeline
          errors={errors}
          asset={asset}
          availableTtmlFiles={availableTtmlFiles}
          subtitlesData={subtitlesData}
          videoAsset={videoAsset}
          videoProxy={videoProxy}
          setLoading={setLoading}
          mode={mode}
          captions={captions}
        />
      ) : (
        <ScribetoolFooter duration={duration} subCount={captions.length} fps={videoFps} />
      )}
    </Stack>
  );
};
